import React from 'react';

/** Plain-English explanations, written for someone seeing this for the first time. */

export function EditionsHelp() {
  return (
    <>
      <p>
        <b>Editions</b> are your commercial plans. Every customer workspace is on exactly one edition.
        Ticking a feature in a column includes it for <i>every</i> workspace on that edition.
      </p>
      <ul>
        <li><b>free</b> - entry level. Just the core basics to get started.</li>
        <li><b>starter</b> - small teams. Adds everyday productivity features.</li>
        <li><b>pro</b> - growing organisations. Automation, scheduling, contractors and AI.</li>
        <li><b>enterprise</b> - large organisations. Analytics, integrations, developer APIs and administration.</li>
      </ul>
      <p className="hint">
        These are the edition keys. The names customers see come from the published price list
        (Pricing), never from here.
      </p>
      <p className="hint">Higher editions normally include everything the lower ones do, plus more.</p>
    </>
  );
}

export function LevelsHelp() {
  return (
    <>
      <p>
        <b>Levels</b> control <i>progressive reveal</i> - how much of the product a workspace sees as it
        grows. This is separate from Editions: an edition is what a customer is <i>entitled</i> to; the level
        is how much is <i>surfaced</i> so far, so new customers aren&apos;t overwhelmed on day one.
      </p>
      <p><b>The four levels</b></p>
      <ul>
        <li><b>L1 · Simple</b> - a brand-new workspace. Only core capabilities are shown.</li>
        <li><b>L2 · Growing</b> - productivity features start to appear.</li>
        <li><b>L3 · Professional</b> - automation, scheduling, contractors and AI appear.</li>
        <li><b>L4 · Enterprise</b> - analytics, integrations, developer tools and administration appear.</li>
      </ul>
      <p><b>How a workspace moves up</b> (automatically, from real usage): it reaches a level once it meets
        <i> all</i> of that level&apos;s minimums.</p>
      <ul>
        <li><b>→ L1 Simple:</b> any new workspace.</li>
        <li><b>→ L2 Growing:</b> ~10 assets, 2+ team members, 2+ active journeys.</li>
        <li><b>→ L3 Professional:</b> ~50 assets, 5+ members, 4+ active journeys.</li>
        <li><b>→ L4 Enterprise:</b> ~250 assets, 15+ members, 6+ active journeys.</li>
      </ul>
      <p>
        When a workspace crosses a threshold, core/free features it&apos;s ready for switch on automatically
        (and never switch off). Paid or advanced features are <i>suggested</i> to that workspace&apos;s admin
        instead of being turned on silently. The Level you set on a row is the point at which that feature
        starts being surfaced.
      </p>
    </>
  );
}

export function CellsHelp() {
  return (
    <>
      <p>Each cell shows whether a feature is included in that edition. Click to toggle.</p>
      <ul>
        <li><span className="cell on" style={{ width: 18, height: 18, verticalAlign: 'middle' }}>✓</span> <b>Included</b></li>
        <li><span className="cell off" style={{ width: 18, height: 18, verticalAlign: 'middle' }}>·</span> <b>Excluded</b></li>
        <li>
          <span className="cell on explicit" style={{ width: 18, height: 18, verticalAlign: 'middle' }}>✓</span>{' '}
          <b>Amber ring = explicitly set.</b> You chose this. Plain cells (no ring) just follow the
          feature&apos;s minimum edition, so you only override the exceptions.
        </li>
        <li>
          <span className="cell on locked" style={{ width: 18, height: 18, verticalAlign: 'middle' }}>🔒</span>{' '}
          <b>Always included.</b> Part of every plan and cannot be switched off: the core of the app (photos, offline,
          notifications, settings) or a row that simply follows its modules, such as most journeys. The reason is shown
          under the row&apos;s name.
        </li>
      </ul>
      <p className="hint">Disabling something that other features depend on will also disable those - the portal warns you first.</p>
      <p className="hint">
        Every feature the app ships has a row here. Unticking one hides it in the app and the server refuses it, before
        any AI credit is spent. Records made before you unticked it stay readable and editable; only making new ones stops.
      </p>
    </>
  );
}

export function RowTypesHelp() {
  return (
    <>
      <p>The rows are your product, grouped by <b>Module</b>. Under each module are its features:</p>
      <ul>
        <li><b>Module</b> - a major area of the app (Assets, Inspections, Jobs, Analytics…).</li>
        <li><b>Feature</b> - a reusable capability (QR codes, AI summary, scheduling…).</li>
        <li><b>Journey</b> - a complete end-to-end process spanning modules (Request → Inspection).</li>
        <li><b>Platform Service</b> - technical infrastructure features sit on (AI platform, storage, search).</li>
      </ul>
    </>
  );
}

/** Collapsible "how this works" banner shown above the matrix. */
export function HowItWorks() {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="howto">
      <button className="howto-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="caret">{open ? '▾' : '▸'}</span>
        New here? How the edition matrix works
      </button>
      {open ? (
        <div className="howto-body">
          <div className="howto-col"><h4>Editions (columns)</h4><EditionsHelp /></div>
          <div className="howto-col"><h4>Levels (progressive reveal)</h4><LevelsHelp /></div>
          <div className="howto-col"><h4>Cells &amp; rows</h4><CellsHelp /><RowTypesHelp /></div>
        </div>
      ) : null}
    </div>
  );
}
